import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FormField } from 'components/layout/form-fields/FormField';
import { observer } from 'mobx-react';
import { jobPositionStore } from '../JobPositionStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import {
	formatTimeAgo,
	getOptionFromValue,
	getValueFromOptions
} from 'shared/utils';
import { Switch } from 'components/layout/form-fields/inputs/Switch';
import { dialogStore } from 'components/layout/modals/DialogStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import DropdownSearchable from 'ats/components/dropdown-searchable/DropdownSearchable';
import MultiItemsList from 'ats/components/multi-items-list/MultiItemsList';

function FormikStateBridge({
	isSubmitting,
	dirty,
	onSubmittingChange,
	onDirtyChange
}) {
	useEffect(() => {
		onSubmittingChange?.(isSubmitting);
	}, [isSubmitting, onSubmittingChange]);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);

	return null;
}

const validationSchema = Yup.object({
	description: Yup.string()
		.required('Description is required')
		.max(1000, 'Description must be at most 1000 characters'),
	workSchedule: Yup.string().required('Work Schedule is required'),
	term: Yup.string().required('Term is required'),
	locationType: Yup.string().required('Location Type is required'),
	responsibilities: Yup.array().of(Yup.string()),
	requirements: Yup.array().of(Yup.string()),
	bonusPoints: Yup.array().of(Yup.string()),
	offers: Yup.array().of(Yup.string()),
	codes: Yup.array().of(Yup.string()),
	additionalComments: Yup.string()
});

const JobPositionDescriptionForm = ({
	data,
	onSuccess,
	forceEditingMode = false
}) => {
	const [isEditing, setIsEditing] = useState(forceEditingMode);
	const [isDirty, setIsDirty] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const formikRef = useRef(null);

	const handleDirtyChange = useCallback(dirty => {
		setIsDirty(dirty);
	}, []);

	const handleSubmittingChange = useCallback(submitting => {
		setIsSubmitting(submitting);
	}, []);

	const cancelEdit = useCallback(() => {
		setIsEditing(false);
		setIsDirty(false);
		setIsSubmitting(false);
	}, []);

	const initialData = {
		description: data?.description || '',
		workSchedule: data?.workSchedule || '',
		term: data?.term || '',
		locationType: data?.locationType || '',
		responsibilities: data?.responsibilities || [],
		requirements: data?.requirements || [],
		bonusPoints: data?.bonusPoints || [],
		offers: data?.offers || [],
		codes: data?.codes || [],
		additionalComments: data?.additionalComments || ''
	};

	const canTogglePublish = data?.status === 'open';

	const blockPublishToggle = event => {
		if (!canTogglePublish) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const toggleJobPositionVisibility = async value => {
		if (!canTogglePublish) {
			return;
		}

		dialogStore.openDialog(
			{
				title: `${value ? 'Unpublish' : 'Publish'} Job Position`,
				message: `The Job Position will be ${value ? 'unpublished' : 'published'}. Are you sure?`,
				confirmLabel: 'Confirm',
				confirmButtonClass: 'is-primary'
			},
			async () => {
				const result = await jobPositionStore.update(data.id, {
					job: {
						disabled: value
					}
				});
				if (result.status === 'success') {
					notificationStore.pushSuccessNotification(
						'Job Position Updated',
						'The Job Position has been successfully updated.'
					);
					onSuccess && onSuccess();
				} else {
					notificationStore.pushErrorNotification(
						'Update Failed',
						'Failed to update the Job Position'
					);
				}
			}
		);
	};

	const handleSubmit = async values => {
		const {
			description,
			workSchedule,
			term,
			locationType,
			responsibilities,
			requirements,
			bonusPoints,
			offers,
			codes,
			additionalComments
		} = values;

		const jobData = {
			description,
			workSchedule,
			term,
			locationType,
			responsibilities,
			requirements,
			bonusPoints,
			offers,
			codes,
			additionalComments
		};

		const result = await jobPositionStore.update(data.id, {
			job: jobData
		});

		if (result.status === 'success') {
			notificationStore.pushSuccessNotification(
				'Job Position Updated',
				'The Job Position has been successfully updated.'
			);
		} else {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update the Job Position'
			);
		}
	};

	return (
		<div className="JobPositionDescriptionForm">
			<div className="job-position-form-header">
				<div
					className={`job-position-publish-controls${
						canTogglePublish ? '' : ' is-disabled'
					}`}
					aria-disabled={!canTogglePublish || undefined}
					title={
						canTogglePublish
							? undefined
							: 'Only open job positions can be published'
					}
					onClickCapture={blockPublishToggle}
					onKeyDownCapture={blockPublishToggle}>
					<Switch
						id="disabled-switch"
						value={!data?.disabled}
						activeLabel="Unpublish"
						inactiveLabel="Publish"
						onValueChange={value => {
							toggleJobPositionVisibility(!value);
						}}
					/>
					{data && !data.disabled && data.status === 'open' ? (
						<button
							type="button"
							className="view-public-page-btn"
							aria-label="View public page"
							onClick={() => window.open(`/jobs/${data.id}`, '_blank')}>
							<img src="/ats/icons/link-external-blue-icon.svg" alt="" />
						</button>
					) : null}
				</div>

				<div className="action-buttons">
					<div className="is-flex gap-1">
						<UserProfileAvatar
							imageUrl={data?.updatedBy?.imageUrl}
							name={data?.updatedBy?.name}
						/>
						<span>
							Updated:{' '}
							{data?.updatedAt
								? formatTimeAgo(new Date(data?.updatedAt))
								: null}
						</span>
					</div>
					{isEditing ? (
						<>
							<button type="button" onClick={cancelEdit} className="cancel-btn">
								Cancel
							</button>
							<button
								type="button"
								disabled={!isDirty || isSubmitting}
								onClick={() => formikRef.current.submitForm()}
								className="save-btn">
								Save
							</button>
						</>
					) : (
						<button onClick={() => setIsEditing(true)} className="edit-btn">
							<img src="/ats/icons/pencil-blue-icon.svg" alt="" />
						</button>
					)}
				</div>
			</div>
			{isEditing ? (
				<Formik
					innerRef={formikRef}
					initialValues={initialData}
					validationSchema={validationSchema}
					onSubmit={async (values, actions) => {
						await handleSubmit(values);
						onSuccess && onSuccess();
						setIsEditing(false);
						setIsDirty(false);
						setIsSubmitting(false);
						actions.setSubmitting(false);
					}}>
					{({ setFieldValue, values, isSubmitting, dirty }) => {
						return (
							<Form className="job-position-form-content">
								<FormikStateBridge
									isSubmitting={isSubmitting}
									dirty={dirty}
									onSubmittingChange={handleSubmittingChange}
									onDirtyChange={handleDirtyChange}
								/>
								{/* Description */}
								<div className="columns">
									<div className="column is-full">
										<FormField
											className="ats-field"
											label="Description"
											field="description"
											type="textarea"
											placeholder="Description"
											maxlength={1000}
										/>
									</div>
								</div>

								{/* Work Schedule */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Work Schedule</label>
											<div className="control">
												<DropdownSearchable
													value={getOptionFromValue(
														values.workSchedule,
														jobPositionStore.workScheduleOptions
													)}
													options={jobPositionStore.workScheduleOptions}
													onChange={value => {
														setFieldValue('workSchedule', value?.value);
													}}
													placeholder="You can choose one option"
												/>
											</div>
											<ErrorMessage
												name="workSchedule"
												component="div"
												className="text-red-600 text-sm"
											/>
										</div>
									</div>
								</div>

								{/* Term */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Term</label>
											<div className="control">
												<DropdownSearchable
													value={getOptionFromValue(
														values.term,
														jobPositionStore.termOptions
													)}
													options={jobPositionStore.termOptions}
													onChange={value => {
														setFieldValue('term', value?.value);
													}}
													placeholder="You can choose one option"
												/>
											</div>
											<ErrorMessage
												name="term"
												component="div"
												className="text-red-600 text-sm"
											/>
										</div>
									</div>
								</div>

								{/* Location Type */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Location Type</label>
											<div className="control">
												<DropdownSearchable
													value={getOptionFromValue(
														values.locationType,
														jobPositionStore.locationTypeOptions
													)}
													options={jobPositionStore.locationTypeOptions}
													onChange={value => {
														setFieldValue('locationType', value?.value);
													}}
													placeholder="You can choose one option"
												/>
											</div>
											<ErrorMessage
												name="locationType"
												component="div"
												className="text-red-600 text-sm"
											/>
										</div>
									</div>
								</div>

								{/* Responsibilities */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Responsibilities</label>
											<FieldArray name="responsibilities">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.responsibilities}
														push={push}
														remove={remove}
														replace={replace}
														errorName="responsibilities"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Requirements */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Requirements</label>
											<FieldArray name="requirements">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.requirements}
														push={push}
														remove={remove}
														replace={replace}
														errorName="requirements"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Bonus Points */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Bonus Points</label>
											<FieldArray name="bonusPoints">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.bonusPoints}
														push={push}
														remove={remove}
														replace={replace}
														errorName="bonusPoints"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Offers */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">What we Offer</label>
											<FieldArray name="offers">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.offers}
														push={push}
														remove={remove}
														replace={replace}
														errorName="offers"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Codes */}
								<div className="columns">
									<div className="column is-full">
										<div className="field has-text-left">
											<label className="subtitle is-7">Codes</label>
											<FieldArray name="codes">
												{({ push, remove, replace }) => (
													<MultiItemsList
														items={values.codes}
														push={push}
														remove={remove}
														replace={replace}
														errorName="codes"
													/>
												)}
											</FieldArray>
										</div>
									</div>
								</div>

								{/* Additional Comments */}
								<div className="columns">
									<div className="column is-full">
										<FormField
											className="ats-field"
											label="Additional Comments"
											field="additionalComments"
											type="textarea"
											placeholder=""
										/>
									</div>
								</div>
							</Form>
						);
					}}
				</Formik>
			) : (
				<div className="job-position-info-content">
					<div className="job-position-info-container">
						<h3>Description</h3>
						<p>{initialData.description}</p>
					</div>

					<div className="job-position-info-container">
						<h3>Work Schedule</h3>
						<div className="tags-container">
							<span>
								{getValueFromOptions(
									initialData.workSchedule,
									jobPositionStore.workScheduleOptions
								)}
							</span>
						</div>
					</div>

					<div className="job-position-info-container">
						<h3>Term</h3>
						<div className="tags-container">
							<span>
								{getValueFromOptions(
									initialData.term,
									jobPositionStore.termOptions
								)}
							</span>
						</div>
					</div>

					<div className="job-position-info-container">
						<h3>Location Type</h3>
						<div className="tags-container">
							<span>
								{getValueFromOptions(
									initialData.locationType,
									jobPositionStore.locationTypeOptions
								)}
							</span>
						</div>
					</div>

					<div className="job-position-info-container">
						<h3>Responsibilities</h3>
						<ul>
							{initialData.responsibilities.map((resp, i) => (
								<li key={i}>{resp}</li>
							))}
						</ul>
					</div>

					<div className="job-position-info-container">
						<h3>Requirements</h3>
						<ul>
							{initialData.requirements.map((req, i) => (
								<li key={i}>{req}</li>
							))}
						</ul>
					</div>

					<div className="job-position-info-container">
						<h3>Bonus Points</h3>
						<ul>
							{initialData.bonusPoints.map((req, i) => (
								<li key={i}>{req}</li>
							))}
						</ul>
					</div>

					<div className="job-position-info-container">
						<h3>What we Offer</h3>
						<ul>
							{initialData.offers.map((req, i) => (
								<li key={i}>{req}</li>
							))}
						</ul>
					</div>

					<div className="job-position-info-container">
						<h3>Codes</h3>
						<ul>
							{initialData.codes.map((req, i) => (
								<li key={i}>{req}</li>
							))}
						</ul>
					</div>

					<div className="job-position-info-container">
						<h3>Additional Comments</h3>
						<p>{initialData.additionalComments}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default observer(JobPositionDescriptionForm);
